// 基础AI服务类 - 提取重复代码
export interface GenerateRequest {
  content: string
  description?: string
  scenario: string
  target: string
  tone: string
  userLanguage: string
  contentLanguage?: string
}

export interface GenerateResponse {
  response: string
  translation?: string
  detectedLanguage?: string
}

export interface StreamResponse {
  content: string
  isComplete: boolean
  translation?: string
  detectedLanguage?: string
  originalResponse?: string
}

export abstract class BaseAIService {
  // 检测文本语言
  protected detectLanguage(text: string): string {
    // 改进的语言检测逻辑
    const chineseRegex = /[\u4e00-\u9fff]/
    const spanishRegex = /[ñáéíóúü¿¡]/i
    const frenchRegex = /[àâäéèêëïîôöùûüÿç]/i
    const germanRegex = /[äöüßÄÖÜ]/i

    // 中文检测（优先级最高）
    if (chineseRegex.test(text)) {
      // 繁体中文特征字符
      const traditionalChars = /[繁體臺灣們個這樣時間問題現實際應該會議討論決定]/
      return traditionalChars.test(text) ? 'zh-tw' : 'zh-cn'
    }
    
    // 其他语言检测（按特征字符强度排序）
    if (spanishRegex.test(text)) return 'es'
    if (frenchRegex.test(text)) return 'fr'
    if (germanRegex.test(text)) return 'de'
    
    // 英语检测（最后检查，因为很多语言都包含英语字符）
    const englishOnlyRegex = /^[a-zA-Z\s.,!?;:'"()\-0-9]+$/
    if (englishOnlyRegex.test(text)) return 'en'
    
    return 'en' // 默认英语
  }

  // 获取语言名称
  protected getLanguageName(code: string): string {
    const languageNames: Record<string, string> = {
      'en': 'English',
      'zh-cn': 'Simplified Chinese',
      'zh-tw': 'Traditional Chinese',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'ja': 'Japanese',
      'ko': 'Korean',
      'pt': 'Portuguese',
      'ru': 'Russian',
      'ar': 'Arabic',
      'hi': 'Hindi',
      'it': 'Italian',
      'nl': 'Dutch',
      'sv': 'Swedish',
      'pl': 'Polish',
      'tr': 'Turkish',
      'th': 'Thai',
      'vi': 'Vietnamese',
      'id': 'Indonesian'
    }
    return languageNames[code] || 'English'
  }

  // 构建智能提示词
  protected buildPrompt(request: GenerateRequest): string {
    const { content, description, scenario, target, tone, userLanguage, contentLanguage } = request
    
    // 检测内容语言
    const detectedLang = contentLanguage || this.detectLanguage(content)
    const contentLangName = this.getLanguageName(detectedLang)
    const userLangName = this.getLanguageName(userLanguage)
    
    // 判断是否需要翻译
    const needsTranslation = detectedLang !== userLanguage
    
    // 构建场景描述
    let scenarioDesc = ""
    if (scenario && scenario !== "none") {
      const scenarioMap: Record<string, string> = {
        'email': 'email communication',
        'message': 'instant message/chat',
        'workplace': 'workplace/professional setting',
        'school': 'academic/school environment',
        'home': 'family/home setting',
        'business': 'business/commercial context',
        'academic': 'academic/scholarly context',
        'social': 'social/casual interaction',
        'formal': 'formal/official communication',
        'casual': 'casual/informal interaction',
        'professional': 'professional/business context',
        'personal': 'personal/private communication',
        'customer-service': 'customer service interaction',
        'interview': 'interview/meeting context',
        'presentation': 'presentation/public speaking'
      }
      scenarioDesc = ` in a ${scenarioMap[scenario] || 'general'} context`
    }

    // 构建目标对象描述
    let targetDesc = ""
    if (target && target !== "none") {
      const targetMap: Record<string, string> = {
        'lover': 'romantic partner',
        'friend': 'friend',
        'boss': 'boss/supervisor',
        'colleague': 'colleague/coworker',
        'classmate': 'classmate/schoolmate',
        'parents': 'parents',
        'child': 'child',
        'teacher': 'teacher/instructor',
        'student': 'student',
        'client': 'client',
        'customer': 'customer',
        'stranger': 'stranger/unknown person',
        'family': 'family member',
        'sibling': 'sibling',
        'neighbor': 'neighbor',
        'mentor': 'mentor/advisor',
        'supplier': 'supplier/vendor',
        'service-provider': 'service provider',
        'partner': 'business partner',
        'subordinate': 'subordinate/team member'
      }
      targetDesc = ` to a ${targetMap[target] || 'person'}`
    }

    // 构建语调描述
    let toneDesc = ""
    if (tone && tone !== "none") {
      const toneMap: Record<string, string> = {
        'humorous': 'humorous and light-hearted',
        'serious': 'serious and professional',
        'happy': 'cheerful and positive',
        'angry': 'firm but controlled',
        'mischievous': 'playful and teasing',
        'polite': 'polite and respectful',
        'casual': 'casual and relaxed',
        'formal': 'formal and professional',
        'friendly': 'friendly and warm',
        'confident': 'confident and assertive',
        'apologetic': 'apologetic and understanding',
        'enthusiastic': 'enthusiastic and energetic'
      }
      toneDesc = ` with a ${toneMap[tone] || 'neutral'} tone`
    }

    // 构建语言要求
    let languageInstructions = ""
    if (needsTranslation) {
      languageInstructions = `

LANGUAGE REQUIREMENTS:
- The original message is in ${contentLangName}
- The user interface language is ${userLangName}
- ALL explanations, descriptions, headings, and context MUST be written in ${userLangName}
- Response options should be in ${contentLangName} (the original message language)
- Provide translations of response options in ${userLangName}
- IMPORTANT: Do not use English for explanations if the user language is not English`
    } else {
      languageInstructions = `

LANGUAGE REQUIREMENTS:
- Both the message and user interface are in ${userLangName}
- ALL content including explanations, responses, and context MUST be in ${userLangName}
- Do not mix languages - use ${userLangName} consistently throughout`
    }

    // 构建基础提示词
    let prompt = `Please help me craft an appropriate response to the following message${scenarioDesc}${targetDesc}${toneDesc}.

Original message:
"${content}"${description ? `

Additional context:
"${description}"` : ''}

Requirements:
1. Provide a thoughtful, contextually appropriate response
2. Consider the relationship, setting, and desired tone${description ? `
3. Take into account the additional context provided` : ''}
${description ? '4' : '3'}. Keep the response natural and authentic
${description ? '5' : '4'}. Match the communication style to the context${languageInstructions}

IMPORTANT FORMATTING INSTRUCTIONS:
Please structure your response using one of these formats:

FORMAT 1 - For structured advice or multiple suggestions:
Use Markdown formatting with clear headings and sections:
# Main Title
## Section 1
Content here...
---
Alternative content...

## Section 2  
Content here...
---
Alternative content...

FORMAT 2 - For multiple response options:
Use numbered lists with clear formatting:
1. **Option Title:**
   "Actual response text here"
   (Context or explanation in parentheses)

2. **Option Title:**
   "Actual response text here"
   (Context or explanation in parentheses)

FORMAT 3 - For single response with explanation:
Provide a clear description followed by the response:
**Response in ${contentLangName}:**
"Your suggested response here"

Additional context or explanation can follow.${needsTranslation ? `

TRANSLATION REQUIREMENT:
If the response suggestions are in ${contentLangName} and the user interface is in ${userLangName}, please add a translation section at the end:

---TRANSLATION---
[Provide translations of the main response options in ${userLangName}]` : ''}

Remember: Choose the most appropriate format based on the content type and stick to it consistently. Use proper Markdown syntax for headings (#, ##) and bold text (**text**) when using structured formats.`

    return prompt
  }

  // 抽象方法 - 子类必须实现
  abstract generateStreamResponse(request: GenerateRequest): AsyncGenerator<StreamResponse, void, unknown>
  abstract generateResponse(request: GenerateRequest): Promise<GenerateResponse>
} 